import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { coursesApi, learningPathsApi, orgApi } from '../../api/client';

const emptyForm = {
  title: '',
  description: '',
  isSequential: true,
  isCertification: false,
  mandatoryForScheduling: false,
  certificationExpirationDays: 365,
};

const LearningPathsPage: React.FC<{ mode?: 'manage' | 'assign' | 'progress' }> = ({ mode = 'manage' }) => {
  const [items, setItems] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<any>(emptyForm);
  const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
  const [assignPath, setAssignPath] = useState<any>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

  const load = async () => {
    try {
      const [p, c, e, a] = await Promise.all([
        learningPathsApi.list(),
        coursesApi.list(),
        orgApi.listEmployees(),
        learningPathsApi.listAssignments(),
      ]);
      setItems(p);
      setCourses(c);
      setEmployees(e);
      setAssignments(a);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (publish: boolean) => {
    try {
      setSubmitting(true);
      setError(null);
      await learningPathsApi.create({
        title: form.title,
        description: form.description,
        isSequential: !!form.isSequential,
        isCertification: !!form.isCertification,
        mandatoryForScheduling: !!form.mandatoryForScheduling,
        certificationExpirationDays: form.isCertification ? Number(form.certificationExpirationDays) || 365 : null,
        status: publish ? 'published' : 'draft',
        courses: selectedCourses.map((item, index) => ({
          courseId: item.courseId,
          order: index + 1,
          isRequired: item.isRequired,
        })),
      });
      setCreating(false);
      setForm(emptyForm);
      setSelectedCourses([]);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const assign = async () => {
    try {
      setSubmitting(true);
      for (const employeeId of selectedEmployeeIds) {
        await learningPathsApi.assign(assignPath.id, { employeeId });
      }
      setAssignPath(null);
      setSelectedEmployeeIds([]);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (creating) {
    const unselected = courses.filter((c) => !selectedCourses.some((s) => s.courseId === c.id));
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Create Learning Path</Typography>
          <Button variant="outlined" onClick={() => setCreating(false)}>Cancel</Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <TextField fullWidth label="Learning Path Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={3} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: 'column' }}>
              <FormControlLabel control={<Switch checked={!!form.isSequential} onChange={(e) => setForm({ ...form, isSequential: e.target.checked })} />} label="Sequential (courses must be completed in order)" />
              <FormControlLabel control={<Switch checked={!!form.mandatoryForScheduling} onChange={(e) => setForm({ ...form, mandatoryForScheduling: e.target.checked })} />} label="Mandatory for Scheduling" />
              <FormControlLabel control={<Switch checked={!!form.isCertification} onChange={(e) => setForm({ ...form, isCertification: e.target.checked })} />} label="Certification (expires and requires recertification)" />
              {form.isCertification && (
                <TextField type="number" label="Certification valid for (days)" value={form.certificationExpirationDays} onChange={(e) => setForm({ ...form, certificationExpirationDays: e.target.value })} size="small" sx={{ maxWidth: 200 }} helperText="e.g. 365 for 1 year" />
              )}
            </Box>
            {!form.isSequential && <Alert severity="info" sx={{ mb: 2 }}>Flexible mode: Employees can take courses in any order.</Alert>}
            {form.mandatoryForScheduling && <Alert severity="warning" sx={{ mb: 2 }}>This learning path is mandatory for scheduling eligibility. Employees must complete this path and have banking information on file to be eligible for scheduling.</Alert>}
            {form.isCertification && <Alert severity="info">This path will expire after {form.certificationExpirationDays} days. Employees will receive reminders before expiration and will be re-assigned automatically when it lapses.</Alert>}
          </CardContent>
        </Card>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Add Courses</Typography>
            {unselected.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel>Select a course to add</InputLabel>
                <Select value="" label="Select a course to add" onChange={(e) => {
                  const course = courses.find((c) => c.id === e.target.value);
                  if (course) setSelectedCourses((prev) => [...prev, { courseId: course.id, course, isRequired: true }]);
                }}>
                  {unselected.map((course) => (
                    <MenuItem key={course.id} value={course.id}>{course.title}{course.duration ? ` (${course.duration})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="info">All available courses have been added to this learning path.</Alert>
            )}
          </CardContent>
        </Card>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Course Sequence ({selectedCourses.length} course{selectedCourses.length === 1 ? '' : 's'})</Typography>
            {selectedCourses.length === 0 ? (
              <Alert severity="warning">No courses added yet. Add at least one course to create the learning path.</Alert>
            ) : (
              <List>
                {selectedCourses.map((item, index) => (
                  <Paper key={item.courseId} elevation={1} sx={{ mb: 1 }}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight="medium">{index + 1}. {item.course.title}</Typography>
                            <Chip
                              label={item.isRequired ? 'Required' : 'Optional'}
                              size="small"
                              color={item.isRequired ? 'primary' : 'default'}
                              onClick={() => setSelectedCourses((prev) => prev.map((c, i) => i === index ? { ...c, isRequired: !c.isRequired } : c))}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Box>
                        }
                        secondary={item.course.description}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" color="error" onClick={() => setSelectedCourses((prev) => prev.filter((_, i) => i !== index))}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" disabled={submitting} onClick={() => create(false)}>Save as Draft</Button>
          <Button variant="contained" disabled={submitting || selectedCourses.length === 0 || !form.title} onClick={() => create(true)}>
            {submitting ? <CircularProgress size={24} /> : 'Publish Learning Path'}
          </Button>
        </Box>
      </Box>
    );
  }

  if (assignPath) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={600}>Assign Learning Path</Typography>
          <Button variant="outlined" onClick={() => setAssignPath(null)}>Cancel</Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{assignPath.title}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>Select employees to assign this learning path.</Typography>
            {employees.map((employee) => (
              <Box key={employee.id} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: selectedEmployeeIds.includes(employee.id) ? '#f0f8ff' : 'white' }}>
                <input
                  type="checkbox"
                  checked={selectedEmployeeIds.includes(employee.id)}
                  onChange={(e) => setSelectedEmployeeIds((ids) => e.target.checked ? [...ids, employee.id] : ids.filter((id) => id !== employee.id))}
                  style={{ marginRight: 12, marginTop: 4 }}
                />
                <Box>
                  <Typography fontWeight={700}>{employee.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{employee.email}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
        <Button variant="contained" disabled={submitting || selectedEmployeeIds.length === 0} onClick={assign}>
          {submitting ? 'Assigning...' : 'Assign Learning Path'}
        </Button>
      </Box>
    );
  }

  const title = mode === 'assign' ? 'Assign Learning Path' : mode === 'progress' ? 'Learning Path Progress' : 'Learning Paths';

  return (
    <Box>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <Typography variant="h4">{title}</Typography>
        {mode === 'manage' && (
          <button type="button" onClick={() => setCreating(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            + Create Learning Path
          </button>
        )}
      </div>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {mode !== 'progress' && items.map((p) => (
        <Box key={p.id} sx={{ p: '1.5rem', mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{p.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Courses: {p.courses?.length || 0} · Cert: {p.isCertification ? 'yes' : 'no'} · Scheduling gate: {p.mandatoryForScheduling ? 'yes' : 'no'}
          </Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => { setAssignPath(p); setSelectedEmployeeIds([]); }}>
            Assign to employee
          </Button>
        </Box>
      ))}
      {(mode === 'progress' || mode === 'assign') && (
        <>
          <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>Assigned paths</Typography>
          {assignments.map((a) => (
            <Box key={a.id} sx={{ p: '1.5rem', mb: 1, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{a.employee?.name} → {a.learningPath?.title}</Typography>
              <Typography variant="body2" color="text.secondary">{a.status} {a.certificationStatus ? `· ${a.certificationStatus}` : ''}</Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default LearningPathsPage;
